class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Immediately toggle --active class on checkbox click for instant visual feedback
    facetForm.addEventListener('change', (event) => {
      if (event.target.classList.contains('esalon-facet-filter__checkbox')) {
        const label = event.target.closest('.esalon-facet-filter__label');
        if (label) {
          label.classList.toggle('esalon-facet-filter__label--active', event.target.checked);
        }
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedCount = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (parsedCount && container) {
      container.innerHTML = parsedCount.innerHTML;
      container.classList.remove('loading');
    }
    if (parsedCount && containerDesktop) {
      containerDesktop.innerHTML = parsedCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const filterSelectors = '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #EsalonMobileFiltersForm .js-filter';
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(filterSelectors);
    const facetDetailsElementsFromDom = document.querySelectorAll(filterSelectors);

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
      '.esalon-facets__active-filters',
      '.esalon-facets__mobile-active',
      '.esalon-drawer__active-filters',
      '.esalon-active-filters',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      // Update ALL matching elements, not just the first one
      document.querySelectorAll(selector).forEach((domElement) => {
        domElement.innerHTML = activeFacetsElement.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
      '.esalon-drawer__trigger-label',
      '.esalon-drawer__count-text',
    ];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm && mobileForm.closest('menu-drawer')) {
      mobileForm.closest('menu-drawer').bindEvents();
    }
  }

  static renderCounts(source, target) {
    // Dawn summary
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    // eSalon summary
    const targetEsalonSummary = target.querySelector('.esalon-facet-filter__summary');
    const sourceEsalonSummary = source.querySelector('.esalon-facet-filter__summary');

    if (sourceEsalonSummary && targetEsalonSummary) {
      targetEsalonSummary.outerHTML = sourceEsalonSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    // Dawn wrap
    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }

    // eSalon content
    const targetEsalonContent = target.querySelector('.esalon-facet-filter__content');
    const sourceEsalonContent = source.querySelector('.esalon-facet-filter__content');

    if (sourceEsalonContent && targetEsalonContent) {
      const showLessBtn = target.querySelector('.esalon-facet-show-less-text');
      const isShowingMore = showLessBtn && showLessBtn.style.display !== 'none';
      if (isShowingMore) {
        sourceEsalonContent
          .querySelectorAll('.esalon-facet-overflow-item')
          .forEach((hiddenItem) => hiddenItem.classList.remove('hidden'));
      }

      targetEsalonContent.outerHTML = sourceEsalonContent.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    // Dawn mobile list
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.rangeLowerBound = this.querySelector('.esalon-facet-price__range-group input:first-child');
    this.rangeHigherBound = this.querySelector('.esalon-facet-price__range-group input:last-child');
    this.textInputLowerBound = this.querySelector('.esalon-facet-price__field:first-child input');
    this.textInputHigherBound = this.querySelector('.esalon-facet-price__field:last-child input');

    if (!this.rangeLowerBound || !this.rangeHigherBound || !this.textInputLowerBound || !this.textInputHigherBound) return;

    const safeNum = (val, fallback) => { const n = parseFloat(val); return isNaN(n) ? fallback : n; };

    // Text input: select on focus for easy editing
    this.textInputLowerBound.addEventListener('focus', () => this.textInputLowerBound.select());
    this.textInputHigherBound.addEventListener('focus', () => this.textInputHigherBound.select());

    // Text input change: validate, sync slider, update gradient
    this.textInputLowerBound.addEventListener('change', (event) => {
      const min = safeNum(event.target.min, 0);
      const max = safeNum(this.textInputHigherBound.value, safeNum(event.target.max, 0)) - 1;
      event.target.value = Math.max(Math.min(safeNum(event.target.value, min), max), min);
      this.rangeLowerBound.value = event.target.value;
      const rangeMax = safeNum(this.rangeLowerBound.max, 1);
      this.rangeLowerBound.parentElement.style.setProperty(
        '--range-min',
        `${(safeNum(this.rangeLowerBound.value, 0) / rangeMax) * 100}%`
      );
    });

    this.textInputHigherBound.addEventListener('change', (event) => {
      const max = safeNum(event.target.max, 0);
      const min = safeNum(this.textInputLowerBound.value, safeNum(event.target.min, 0)) + 1;
      event.target.value = Math.min(Math.max(safeNum(event.target.value, max), min), max);
      this.rangeHigherBound.value = event.target.value;
      const rangeMax = safeNum(this.rangeHigherBound.max, 1);
      this.rangeHigherBound.parentElement.style.setProperty(
        '--range-max',
        `${(safeNum(this.rangeHigherBound.value, 0) / rangeMax) * 100}%`
      );
    });

    // Slider change: sync text input and trigger form submission
    this.rangeLowerBound.addEventListener('change', (event) => {
      this.textInputLowerBound.value = event.target.value;
      this.textInputLowerBound.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.rangeHigherBound.addEventListener('change', (event) => {
      this.textInputHigherBound.value = event.target.value;
      this.textInputHigherBound.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Slider input (live drag): update gradient + sync text, prevent overlap
    this.rangeLowerBound.addEventListener('input', (event) => {
      const upperVal = safeNum(this.textInputHigherBound.value, safeNum(event.target.max, 0));
      event.target.value = Math.min(safeNum(event.target.value, 0), upperVal - 1);
      const rangeMax = safeNum(event.target.max, 1);
      event.target.parentElement.style.setProperty(
        '--range-min',
        `${(safeNum(event.target.value, 0) / rangeMax) * 100}%`
      );
      this.textInputLowerBound.value = event.target.value;
    });

    this.rangeHigherBound.addEventListener('input', (event) => {
      const lowerVal = safeNum(this.textInputLowerBound.value, safeNum(event.target.min, 0));
      event.target.value = Math.max(safeNum(event.target.value, 0), lowerVal + 1);
      const rangeMax = safeNum(event.target.max, 1);
      event.target.parentElement.style.setProperty(
        '--range-max',
        `${(safeNum(event.target.value, 0) / rangeMax) * 100}%`
      );
      this.textInputHigherBound.value = event.target.value;
    });
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    // If no form found, let the link navigate normally
    if (!form) {
      return;
    }
    event.preventDefault();
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class EsalonTagFilters extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Desktop buttons
    this.filters = this.querySelectorAll('.esalon-tag-filter__btn');
    this.filters.forEach((filter) => {
      filter.addEventListener('click', this.onFilterClick.bind(this), { signal });
    });

    // Mobile custom dropdown
    this.dropdown = this.querySelector('.esalon-tag-dropdown');
    this.dropdownTrigger = this.querySelector('.esalon-tag-dropdown__trigger');
    this.dropdownValue = this.querySelector('.esalon-tag-dropdown__value');
    this.dropdownList = this.querySelector('.esalon-tag-dropdown__list');
    this.dropdownOptions = this.querySelectorAll('.esalon-tag-dropdown__option');
    this.focusedIndex = -1;

    if (this.dropdownTrigger) {
      this.dropdownTrigger.addEventListener('click', this.toggleDropdown.bind(this), { signal });
      this.dropdownOptions.forEach((option) => {
        option.addEventListener('click', this.onDropdownSelect.bind(this), { signal });
      });

      document.addEventListener('click', (e) => {
        if (this.dropdown && !this.dropdown.contains(e.target)) this.closeDropdown();
      }, { signal });

      document.addEventListener('keydown', (e) => {
        if (!this.dropdown || !this.dropdown.classList.contains('is-open')) return;
        if (e.key === 'Escape') { this.closeDropdown(); this.dropdownTrigger.focus(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); this.moveFocus(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.moveFocus(-1); }
        else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.focusedIndex >= 0) this.dropdownOptions[this.focusedIndex].click();
        }
      }, { signal });
    }

    // Track selected tag for re-application after AJAX updates
    this.selectedTag = 'all';

    // Observe ProductCount for changes from facet filtering so we always have a fresh baseline
    this.captureProductCount();
    const countEl = document.getElementById('ProductCount');
    if (countEl) {
      this.countObserver = new MutationObserver(() => this.captureProductCount());
      this.countObserver.observe(countEl, { childList: true, characterData: true, subtree: true });
    }

    // Observe ProductGridContainer for AJAX re-renders (Search & Discovery facets/sort)
    // Re-apply the current tag filter to the new DOM
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      this.gridObserver = new MutationObserver(() => {
        if (this.selectedTag !== 'all') {
          this.applyFilter(this.selectedTag);
        }
      });
      this.gridObserver.observe(gridContainer, { childList: true });
    }
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.countObserver) this.countObserver.disconnect();
    if (this.gridObserver) this.gridObserver.disconnect();
  }

  captureProductCount() {
    const countEl = document.getElementById('ProductCount');
    this.originalCountText = countEl ? countEl.textContent : '';
  }

  moveFocus(direction) {
    const opts = this.dropdownOptions;
    if (!opts.length) return;
    this.focusedIndex = Math.max(0, Math.min(opts.length - 1, this.focusedIndex + direction));
    opts.forEach((opt, i) => opt.classList.toggle('esalon-tag-dropdown__option--focused', i === this.focusedIndex));
    opts[this.focusedIndex].scrollIntoView({ block: 'nearest' });
  }

  toggleDropdown(e) {
    e.stopPropagation();
    if (this.dropdown.classList.contains('is-open')) {
      this.closeDropdown();
    } else {
      this.dropdownList.removeAttribute('hidden');
      this.dropdown.classList.add('is-open');
      this.dropdownTrigger.setAttribute('aria-expanded', 'true');
      this.focusedIndex = -1;
    }
  }

  closeDropdown() {
    if (!this.dropdown) return;
    this.dropdownList.setAttribute('hidden', '');
    this.dropdown.classList.remove('is-open');
    this.dropdownTrigger.setAttribute('aria-expanded', 'false');
    this.dropdownOptions.forEach((opt) => opt.classList.remove('esalon-tag-dropdown__option--focused'));
    this.focusedIndex = -1;
  }

  onDropdownSelect(event) {
    const selectedTag = event.currentTarget.dataset.tag;
    const label = event.currentTarget.textContent.trim();

    this.dropdownValue.textContent = label;
    this.dropdownOptions.forEach((opt) => {
      const isActive = opt === event.currentTarget;
      opt.classList.toggle('esalon-tag-dropdown__option--active', isActive);
      opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.closeDropdown();

    this.filters.forEach((filter) => {
      const isActive = filter.dataset.tag === selectedTag;
      filter.classList.toggle('esalon-tag-filter__btn--active', isActive);
      filter.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.applyFilter(selectedTag);
  }

  applyFilter(selectedTag) {
    this.selectedTag = selectedTag;
    const gridItems = document.querySelectorAll('#product-grid .grid__item');
    let visibleCount = 0;

    gridItems.forEach((item) => {
      if (selectedTag === 'all') {
        item.classList.remove('grid__item--tag-hidden');
        visibleCount++;
      } else {
        let tags = [];
        try { tags = JSON.parse(item.dataset.tags || '[]'); } catch (_e) { tags = []; }
        tags = tags.map((t) => t.toLowerCase());
        const filterTags = selectedTag.split('++').map((t) => t.trim().toLowerCase());
        const match = filterTags.some((ft) => tags.includes(ft));
        item.classList.toggle('grid__item--tag-hidden', !match);
        if (match) visibleCount++;
      }
    });

    const countEl = document.getElementById('ProductCount');
    if (countEl) {
      if (selectedTag === 'all') {
        countEl.textContent = this.originalCountText;
      } else {
        countEl.textContent = this.originalCountText.replace(/\d+/, visibleCount);
      }
    }

    // Show/hide no-results message
    const noResults = document.getElementById('TagFilterNoResults');
    if (noResults) {
      if (visibleCount === 0 && selectedTag !== 'all') {
        noResults.removeAttribute('hidden');
      } else {
        noResults.setAttribute('hidden', '');
      }
    }
  }

  onFilterClick(event) {
    const selectedTag = event.currentTarget.dataset.tag;

    this.filters.forEach((filter) => {
      const isActive = filter === event.currentTarget;
      filter.classList.toggle('esalon-tag-filter__btn--active', isActive);
      filter.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.dropdownValue) {
      this.dropdownValue.textContent = event.currentTarget.textContent.trim();
      this.dropdownOptions.forEach((opt) => {
        const isActive = opt.dataset.tag === selectedTag;
        opt.classList.toggle('esalon-tag-dropdown__option--active', isActive);
        opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    this.applyFilter(selectedTag);
  }
}

customElements.define('esalon-tag-filters', EsalonTagFilters);
